import ImagingScheduleService from 'business/services/ImagingScheduleService';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CaptureProfile,
  ImagingSchedulesDTO,
  PlateInspectionsToScoreDTO,
  PlatesToImageDTO,
  Robot,
  SaveCaptureProfilesDTO,
} from 'business/models/Models';
import {
  useImagingSettingsRowNum,
  useSelectedImagingScheduleID,
} from 'redux/hooks/imagingSetup';
import CaptureProfileService from 'business/services/CaptureProfileService';
import { cloneDeep } from 'lodash';
import { CaptureProfileType, TableSortState } from './types';
import ImagingSettingsService from 'business/services/ImagingSettingsService';
import { useRMDispatch } from 'redux/hooks';
import {
  setImagingSettingsDTO,
  setSelectedImagingSettingsID,
} from 'redux/reducers/imagingSettingsReducer';
import { getImageManagementTasks } from 'redux/thunks/imageManagementTask';
import { getAllImagerQuota } from 'redux/thunks/imagerQuota';
import { FmlxTable } from 'fmlx-common-ui';

const imsService = new ImagingScheduleService();
const imagingSettingsService = new ImagingSettingsService();
export const useImagingSchedules = () => {
  const [refetch, setRefetch] = useState<boolean>(false);
  const [imagingSchedulesDto, setImagingSchedulesDto] = useState<ImagingSchedulesDTO[]>();
  const [imagingSchedulesOriginal, setImagingSchedulesOriginal] =
    useState<ImagingSchedulesDTO[]>();

  const refetchImagingSchedules = () => {
    setRefetch(!refetch);
  };

  useEffect(() => {
    const fetchData = async () => {
      imsService.getImagingSchedules().then((data) => {
        setImagingSchedulesDto(data);
        setImagingSchedulesOriginal(data);
      });
    };
    fetchData();
  }, [refetch]);

  return {
    imagingSchedulesDto,
    setImagingSchedulesDto,
    imagingSchedulesOriginal,
    refetchImagingSchedules,
  };
};

export const fetchImageManagementTasks = () => {
  const dispatch = useRMDispatch();
  const [refetch, setRefetch] = useState<boolean>(false);
  const refetchImageTaskTreeNodes = () => {
    setRefetch(!refetch);
  };

  useEffect(() => {
    dispatch(getImageManagementTasks());
  }, [refetch]);

  return {
    refetchImageTaskTreeNodes,
  };
};

export const fetchImagerQuota = () => {
  const dispatch = useRMDispatch();
  const [refetch, setRefetch] = useState<boolean>(false);
  const refetchImagerQuota = () => {
    setRefetch(!refetch);
  };

  useEffect(() => {
    dispatch(getAllImagerQuota());
  }, [refetch]);

  return {
    refetchImagerQuota,
  };
};

export const useCaptureProfiles = () => {
  const [captureProfiles, setCaptureProfiles] = useState<CaptureProfile[]>();
  useEffect(() => {
    const fetchData = async () => {
      imsService.getCaptureProfiles().then((data) => {
        setCaptureProfiles(data);
      });
    };
    fetchData();
  }, []);

  return {
    captureProfiles,
  };
};

export const useSelectedImagingSchedule = () => {
  const id = useSelectedImagingScheduleID();
  return id;
};

export const usePlatesToImage = () => {
  const [refetch, setRefetch] = useState<boolean>(false);
  const [platesToImage, setPlatesToImage] = useState<PlatesToImageDTO[]>();
  const [platesToImageOriginal, setPlatesToImageOriginal] =
    useState<PlatesToImageDTO[]>();
  const refetchPlatesToImage = () => {
    setRefetch(!refetch);
  };
  useEffect(() => {
    const fetchData = async () => {
      imsService.getPlatesToImage().then((data) => {
        setPlatesToImage(data);
        setPlatesToImageOriginal(data);
      });
    };
    fetchData();
  }, [refetch]);
  return {
    platesToImage,
    platesToImageOriginal,
    setPlatesToImage,
    refetchPlatesToImage,
  };
};

export const usePlateInspectionsToScore = () => {
  const [refetch, setRefetch] = useState<boolean>(false);
  const [plateInspectionsToScore, setPlateInspectionsToScore] =
    useState<PlateInspectionsToScoreDTO[]>();
  const [plateInspectionsToScoreOriginal, setPlateInspectionsToScoreOriginal] =
    useState<PlateInspectionsToScoreDTO[]>();
  const refetchPlateInspectionsToScore = () => {
    setRefetch(!refetch);
  };
  useEffect(() => {
    const fetchData = async () => {
      imsService.getPlateInspectionsToScore().then((data) => {
        setPlateInspectionsToScore(data);
        setPlateInspectionsToScoreOriginal(data);
      });
    };
    fetchData();
  }, [refetch]);
  return {
    plateInspectionsToScore,
    plateInspectionsToScoreOriginal,
    setPlateInspectionsToScore,
    refetchPlateInspectionsToScore,
  };
};

export const useImagingSettings = () => {
  const [originalImgSettings, setOriginalImgSettings] = useState<CaptureProfileType>({});
  const [currentImgSettings, setCurrentImgSettings] = useState<CaptureProfileType>({});
  const [refetch, setRefetch] = useState<boolean>(false);
  const selectedRows = useImagingSettingsRowNum();
  const dispatch = useRMDispatch();

  const refetchImagingSettings = () => {
    setRefetch(!refetch);
  };
  useEffect(() => {
    async function fetchData(): Promise<SaveCaptureProfilesDTO> {
      const captureProfileService = new CaptureProfileService();
      return captureProfileService.getImageSettings();
    }

    (async () => {
      const captureProfiles = await fetchData();
      const captureProfilesMap = {};
      captureProfiles.captureProfilesDto.forEach((cp) => {
        captureProfilesMap[cp.id] = cp;
      });
      dispatch(setImagingSettingsDTO(captureProfiles));
      setCurrentImgSettings(captureProfilesMap);
      setOriginalImgSettings(cloneDeep(captureProfilesMap));
      const cpKeys = Object.keys(captureProfilesMap);
      const cpId = cpKeys?.[selectedRows?.[0]];
      if (cpId) {
        dispatch(setSelectedImagingSettingsID([cpId]));
      }
    })();
  }, [refetch]);
  return {
    originalImgSettings,
    currentImgSettings,
    setCurrentImgSettings,
    refetchImagingSettings,
  };
};

export const FMLX_TABLE_PAGE_SIZES = [50, 100, 200];

type FmlxTablePage = {
  pageIndex: number;
  pageSize: number;
};

/**
 * FmlxTable paginates manually: it renders every row in `data` and only reports
 * the current page through `fetchData`. Callers pass the full sorted list here
 * and spread `paginationProps` onto the table.
 */
export const useFmlxTablePagination = <T,>(
  rows: T[],
  options?: {
    resetKey?: string | number | null;
    onPageChange?: () => void;
  },
) => {
  const resetKey = options?.resetKey;
  const [page, setPage] = useState<FmlxTablePage>({
    pageIndex: 0,
    pageSize: FMLX_TABLE_PAGE_SIZES[0],
  });
  const [seenResetKey, setSeenResetKey] = useState(resetKey);
  const onPageChangeRef = useRef(options?.onPageChange);
  onPageChangeRef.current = options?.onPageChange;

  if (resetKey !== seenResetKey) {
    setSeenResetKey(resetKey);
    setPage((current) =>
      current.pageIndex === 0 ? current : { ...current, pageIndex: 0 },
    );
  }

  const fetchData = useCallback((next: FmlxTablePage) => {
    if (!FMLX_TABLE_PAGE_SIZES.includes(next.pageSize)) {
      return;
    }
    setPage((current) => {
      if (current.pageIndex === next.pageIndex && current.pageSize === next.pageSize) {
        return current;
      }
      return { pageIndex: next.pageIndex, pageSize: next.pageSize };
    });
  }, []);

  const skipInitialPageChange = useRef(true);
  useEffect(() => {
    if (skipInitialPageChange.current) {
      skipInitialPageChange.current = false;
      return;
    }
    onPageChangeRef.current?.();
  }, [page.pageIndex, page.pageSize]);

  const pageStart = page.pageIndex * page.pageSize;
  const pageRows = useMemo(
    () => rows.slice(pageStart, pageStart + page.pageSize),
    [rows, pageStart, page.pageSize],
  );
  const pageCount = page.pageSize > 0 ? Math.ceil(rows.length / page.pageSize) : 0;
  const toPageRowIndexes = useCallback(
    (selectedIndexes: number[]) =>
      selectedIndexes
        .map((index) => index - pageStart)
        .filter((index) => index >= 0 && index < pageRows.length),
    [pageRows.length, pageStart],
  );
  const toGlobalRowIndexes = useCallback(
    (pageRowIds: Array<number | string>) =>
      pageRowIds.map((rowId) => pageStart + Number(rowId)),
    [pageStart],
  );

  return {
    pageRows,
    pageStart,
    toPageRowIndexes,
    toGlobalRowIndexes,
    paginationProps: {
      data: pageRows,
      itemsTotal: rows.length,
      showPagination: true,
      pageSizes: FMLX_TABLE_PAGE_SIZES,
      fetchData,
      // FmlxTable assigns this prop to react-table pageCount. Next does nothing while it is 0.
      pageIndex: pageCount,
    },
  };
};

export const useTableSort = (onSort?: () => void) => {
  const [sortState, setSortState] = useState<TableSortState>(null);

  const onTableSorted = ({
    column,
    type,
  }: {
    column: string;
    type: FmlxTable.SortType;
  }) => {
    setSortState(type ? { column, type } : null);
    onSort?.();
  };

  return { sortState, onTableSorted };
};

export const useRobots = () => {
  const [robots, setRobots] = useState<Robot[]>();

  useEffect(() => {
    const fetchData = async () => {
      imagingSettingsService.getRobots().then((data) => {
        setRobots(data);
      });
    };
    fetchData();
  }, []);

  return robots;
};
